// src/pages/POShip.jsx
import React, { useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../services/firebase";

const CURRENCY = "XOF";
const DEFAULT_ORIGIN = "Fournisseur";
const DEFAULT_DEST = "Boutique";

// function rid() {
//   return (crypto?.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
// }
function rid() {
  let c;
  if (typeof window !== "undefined" && window.crypto) c = window.crypto;      // navigateur
  else if (typeof global !== "undefined" && global.crypto) c = global.crypto; // Node éventuel

  if (c && typeof c.randomUUID === "function") return c.randomUUID();

  const t = (typeof performance !== "undefined" && performance.now)
    ? performance.now()
    : Date.now();
  return `${Math.floor(t).toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function remainToShip(line) {
  const qO = Number(line.qtyOrdered || 0);
  const qR = Number(line.qtyReceived || 0);
  const qS = Number(line.shippedQty || 0);
  return Math.max(0, qO - Math.max(qR, qS)); // on évite tout dépassement
}

export default function POShip() {
  const [mode, setMode] = useState("byPO"); // 'byPO' | 'byItem'
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ------ Métadonnées shipment ------
  const [origin, setOrigin] = useState(DEFAULT_ORIGIN);
  const [destination, setDestination] = useState(DEFAULT_DEST);
  const [carrier, setCarrier] = useState("");
  const [tracking, setTracking] = useState("");
  const [etd, setEtd] = useState(""); // yyyy-mm-dd
  const [eta, setEta] = useState("");
  const [notes, setNotes] = useState("");

  // ------ Mode PAR PO ------
  const [poId, setPoId] = useState("");
  const [po, setPo] = useState(null);     // doc PO
  const [poLines, setPoLines] = useState([]); // lignes enrichies + qtyToShip saisi

  // ------ Mode PAR ARTICLE ------
  const [freeLines, setFreeLines] = useState([]); // [{rowId,itemId,name,barcode,unitCost,qtyToShip,poId?}]
  const [term, setTerm] = useState("");
  const [sugs, setSugs] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  // ======== CHARGER UN PO ========
  async function loadPO() {
    setMsg("");
    setPo(null);
    setPoLines([]);
    if (!poId.trim()) return;
    try {
      setLoading(true);
      const ref = doc(db, "purchaseOrders", poId.trim());
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setMsg("PO introuvable.");
        return;
      }
      const data = snap.data() || {};
      const src = Array.isArray(data.items) ? data.items : [];
      const lines = src.map((l) => ({
        rowId: rid(),
        itemId: l.itemId,
        name: l.name || "Article",
        barcode: l.barcode || "",
        unitCost: Number(l.unitCost || 0),
        qtyOrdered: Number(l.qtyOrdered || 0),
        qtyReceived: Number(l.qtyReceived || 0),
        shippedQty: Number(l.shippedQty || 0),
        qtyToShip: 0, // saisi par l'utilisateur
      }));
      setPo({ id: snap.id, ...data });
      setPoLines(lines);
    } catch (e) {
      console.error(e);
      setMsg("Erreur de chargement du PO.");
    } finally {
      setLoading(false);
    }
  }

  // ======== RECHERCHE ARTICLE (mode byItem) ========
  async function searchItem() {
    setMsg("");
    const t = term.trim();
    setSugs([]);
    if (!t) return;

    setSearching(true);
    try {
      // 1) code-barres exact
      const qCb = query(collection(db, "itemsEK"), where("barcode", "==", t), limit(1));
      const snapCb = await getDocs(qCb);
      if (!snapCb.empty) {
        const d = snapCb.docs[0];
        const it = d.data();
        addFreeLine({
          itemId: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          unitCost: Number(it.cost || 0),
        });
        setTerm("");
        setSugs([]);
        return;
      }
      // 2) nom (prefix sur nameLower)
      const lower = t.toLowerCase();
      const qy = query(
        collection(db, "itemsEK"),
        orderBy("nameLower"),
        startAt(lower),
        endAt(lower + "\uf8ff"),
        limit(10)
      );
      const snap = await getDocs(qy);
      const arr = [];
      snap.forEach((d) => {
        const it = d.data();
        arr.push({
          id: d.id,
          name: it.name || it.title || "Article",
          barcode: it.barcode || "",
          unitCost: Number(it.cost || 0),
        });
      });
      setSugs(arr);
    } catch (e) {
      console.error(e);
      setMsg("Erreur recherche article.");
    } finally {
      setSearching(false);
    }
  }

  function pickSuggestion(s) {
    addFreeLine({
      itemId: s.id,
      name: s.name,
      barcode: s.barcode || "",
      unitCost: Number(s.unitCost || s.cost || 0),
    });
    setTerm("");
    setSugs([]);
    inputRef.current?.focus();
  }

  function addFreeLine({ itemId, name, barcode, unitCost }) {
    setFreeLines((prev) => {
      const f = prev.find((x) => x.itemId === itemId && !x.poId); // même article hors-PO
      if (f) {
        return prev.map((x) =>
          x === f ? { ...x, qtyToShip: Number(x.qtyToShip || 0) + 1 } : x
        );
      }
      return [
        ...prev,
        {
          rowId: rid(),
          itemId,
          name: name || "Article",
          barcode: barcode || "",
          unitCost: Number(unitCost || 0),
          qtyToShip: 1,
          poId: "", // optionnel: associer un PO spécifique pour incrément shippedQty
        },
      ];
    });
  }

  // ======== TOTAUX ========
  const totalsPO = useMemo(() => {
    const qty = poLines.reduce((s, r) => s + Number(r.qtyToShip || 0), 0);
    const cost = poLines.reduce((s, r) => s + Number(r.qtyToShip || 0) * Number(r.unitCost || 0), 0);
    return { qty, cost };
  }, [poLines]);

  const totalsFree = useMemo(() => {
    const qty = freeLines.reduce((s, r) => s + Number(r.qtyToShip || 0), 0);
    const cost = freeLines.reduce((s, r) => s + Number(r.qtyToShip || 0) * Number(r.unitCost || 0), 0);
    return { qty, cost };
  }, [freeLines]);

  // ======== CRÉER LE SHIPMENT ========
  async function handleCreateShipment() {
    setMsg("");
    // validations basiques
    let linesPayload = [];
    if (mode === "byPO") {
      if (!po?.id) {
        setMsg("Sélectionne un PO.");
        return;
      }
      const chosen = poLines.filter((l) => Number(l.qtyToShip || 0) > 0);
      if (!chosen.length) {
        setMsg("Aucune ligne à expédier.");
        return;
      }
      // contrôles de bornes
      for (const l of chosen) {
        const maxShip = remainToShip(l);
        if (Number(l.qtyToShip) > maxShip) {
          setMsg(`Quantité à expédier trop élevée pour ${l.name} (max ${maxShip}).`);
          return;
        }
      }
      linesPayload = chosen.map((l) => ({
        itemId: l.itemId,
        name: l.name,
        barcode: l.barcode || "",
        unitCost: Number(l.unitCost || 0),
        qty: Number(l.qtyToShip || 0),
        poId: po.id,
      }));
    } else {
      const chosen = freeLines.filter((l) => Number(l.qtyToShip || 0) > 0);
      if (!chosen.length) {
        setMsg("Aucune ligne à expédier.");
        return;
      }
      linesPayload = chosen.map((l) => ({
        itemId: l.itemId,
        name: l.name,
        barcode: l.barcode || "",
        unitCost: Number(l.unitCost || 0),
        qty: Number(l.qtyToShip || 0),
        poId: l.poId?.trim() || null, // optionnel
      }));
    }

    try {
      setLoading(true);
      // Transaction : crée shipment + met à jour shippedQty dans les PO concernés (sans toucher onOrder)
      await runTransaction(db, async (tx) => {
        // 1) Prépare la nouvelle expédition
        const shipRef = doc(collection(db, "shipments"));
        const code = `SHP-${Date.now()}`; // simple code lisible
        const header = {
          code,
          origin: origin || DEFAULT_ORIGIN,
          destination: destination || DEFAULT_DEST,
          carrier: carrier || null,
          tracking: tracking || null,
          etd: etd || null, // date string
          eta: eta || null,
          notes: notes || null,
          status: "in_transit", // in_transit | received | canceled
          createdAt: serverTimestamp(),
        };

        // 2) Mise à jour shippedQty par PO (regrouper par poId)
        const byPO = new Map();
        linesPayload.forEach((ln) => {
          if (!ln.poId) return; // lignes hors-PO, pas d'impact PO
          const key = String(ln.poId);
          if (!byPO.has(key)) byPO.set(key, []);
          byPO.get(key).push(ln);
        });

        // Lire tous les PO qu'on doit modifier
        const poIds = Array.from(byPO.keys());
        const poRefs = poIds.map((id) => doc(db, "purchaseOrders", id));
        const poSnaps = await Promise.all(poRefs.map((r) => tx.get(r)));

        // Pour chaque PO, contrôler et incrémenter shippedQty par item
        for (let i = 0; i < poRefs.length; i++) {
          const r = poRefs[i];
          const snap = poSnaps[i];
          if (!snap.exists()) throw new Error(`PO introuvable (${r.id})`);
          const data = snap.data() || {};
          const oldItems = Array.isArray(data.items) ? data.items : [];

          // Index par itemId
          const idx = new Map();
          oldItems.forEach((l) => l?.itemId && idx.set(l.itemId, l));

          // Contrôles de bornes
          for (const ln of byPO.get(r.id)) {
            const L = idx.get(ln.itemId);
            if (!L) throw new Error(`Ligne PO manquante pour ${ln.itemId} dans PO ${r.id}`);
            const remain = Math.max(0, Number(L.qtyOrdered || 0) - Math.max(Number(L.qtyReceived || 0), Number(L.shippedQty || 0)));
            if (ln.qty > remain) {
              throw new Error(`Expédition dépasse le restant du PO ${r.id} pour ${L.name || ln.itemId} (reste ${remain})`);
            }
          }

          // Appliquer les incréments shippedQty
          const newItems = oldItems.map((L) => {
            const add = byPO.get(r.id).filter((ln) => ln.itemId === L.itemId)
              .reduce((s, ln) => s + Number(ln.qty || 0), 0);
            if (!add) return L;
            return {
              ...L,
              shippedQty: Number(L.shippedQty || 0) + add,
            };
          });

          tx.update(r, {
            items: newItems,
            updatedAt: serverTimestamp(),
          });
        }

        // 3) Écrire le shipment (avec lignes embarquées)
        tx.set(shipRef, {
          ...header,
          lines: linesPayload, // [{itemId,name,barcode,unitCost,qty,poId?}]
        });
      });

      // reset UI
      if (mode === "byPO") {
        setPo(null);
        setPoLines([]);
        setPoId("");
      } else {
        setFreeLines([]);
        setTerm("");
        setSugs([]);
      }
      setCarrier("");
      setTracking("");
      setEtd("");
      setEta("");
      setNotes("");

      setMsg("✅ Expédition créée (statut: en transit).");
    } catch (e) {
      console.error(e);
      setMsg("❌ " + e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">Créer un Shipping</h3>
        <div className="d-flex gap-2">
          <select
            className="form-select"
            style={{ width: 180 }}
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="byPO">Ship par PO</option>
            <option value="byItem">Ship par article</option>
          </select>
          <button className="btn btn-dark" type="button" onClick={handleCreateShipment} disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Créer l'expédition
          </button>
        </div>
      </div>

      {/* Métadonnées expédition */}
      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-12 col-md-3">
            <label className="form-label">Origine</label>
            <input className="form-control" value={origin} onChange={(e) => setOrigin(e.target.value)} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Destination</label>
            <input className="form-control" value={destination} onChange={(e) => setDestination(e.target.value)} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Transporteur</label>
            <input className="form-control" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Tracking</label>
            <input className="form-control" value={tracking} onChange={(e) => setTracking(e.target.value)} />
          </div>

          <div className="col-6 col-md-3">
            <label className="form-label">ETD (départ)</label>
            <input type="date" className="form-control" value={etd} onChange={(e) => setEtd(e.target.value)} />
          </div>
          <div className="col-6 col-md-3">
            <label className="form-label">ETA (arrivée)</label>
            <input type="date" className="form-control" value={eta} onChange={(e) => setEta(e.target.value)} />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">Notes</label>
            <input className="form-control" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
      </div>

      {mode === "byPO" ? (
        <>
          {/* Sélection PO */}
          <div className="card mb-3">
            <div className="card-body row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label">PO ID</label>
                <div className="d-flex gap-2">
                  <input
                    className="form-control"
                    placeholder="ex: PO-2025-001 (docId Firestore)"
                    value={poId}
                    onChange={(e) => setPoId(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" type="button" onClick={loadPO} disabled={loading}>
                    Charger
                  </button>
                </div>
                <div className="form-text">Règle: on ne réduit pas <code>onOrder</code> au shipping, seulement à la réception.</div>
              </div>
              <div className="col-12 col-md-6">
                {po ? (
                  <div className="p-2 border rounded small">
                    <div className="fw-semibold">PO: {poId}</div>
                    <div>Fournisseur: {po.supplier || "—"} · Statut: {po.status || "open"}</div>
                    <div className="text-muted">
                      Lignes: {Array.isArray(po.items) ? po.items.length : 0}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted small">Aucun PO chargé.</div>
                )}
              </div>
            </div>
          </div>

          {/* Lignes du PO */}
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Article</th>
                      <th>CB</th>
                      <th className="text-end">Cmd</th>
                      <th className="text-end">Réç.</th>
                      <th className="text-end">Déjà ship</th>
                      <th className="text-end">Reste ship</th>
                      <th className="text-end" style={{ width: 140 }}>À expédier</th>
                      <th className="text-end">Coût ({CURRENCY})</th>
                      <th className="text-end">Total ({CURRENCY})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {poLines.length === 0 ? (
                      <tr><td colSpan={9} className="text-muted">Aucune ligne</td></tr>
                    ) : poLines.map((l) => {
                      const rest = remainToShip(l);
                      const q = Number(l.qtyToShip || 0);
                      const total = q * Number(l.unitCost || 0);
                      const over = q > rest;
                      return (
                        <tr key={l.rowId} className={over ? "table-danger" : ""}>
                          <td>
                            <div className="fw-semibold">{l.name}</div>
                            <div className="small text-muted">{l.itemId}</div>
                          </td>
                          <td>{l.barcode || "—"}</td>
                          <td className="text-end">{Number(l.qtyOrdered || 0).toLocaleString()}</td>
                          <td className="text-end">{Number(l.qtyReceived || 0).toLocaleString()}</td>
                          <td className="text-end">{Number(l.shippedQty || 0).toLocaleString()}</td>
                          <td className="text-end fw-semibold">{rest.toLocaleString()}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={rest}
                              className="form-control text-end"
                              value={l.qtyToShip}
                              onChange={(e) =>
                                setPoLines((prev) =>
                                  prev.map((x) =>
                                    x.rowId === l.rowId
                                      ? { ...x, qtyToShip: Number(e.target.value || 0) }
                                      : x
                                  )
                                )
                              }
                            />
                          </td>
                          <td className="text-end">{Number(l.unitCost || 0).toLocaleString()}</td>
                          <td className="text-end fw-semibold">{total.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={6} className="text-end">Totaux</td>
                      <td className="text-end fw-bold">{totalsPO.qty.toLocaleString()}</td>
                      <td></td>
                      <td className="text-end fw-bold">{totalsPO.cost.toLocaleString()} {CURRENCY}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {msg && (
                <div
                  className={`mt-3 alert py-2 ${
                    msg.startsWith("✅") ? "alert-success" : msg.startsWith("❌") ? "alert-danger" : "alert-info"
                  }`}
                >
                  {msg}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Mode par article (hors-PO ou lié à un PO par ligne) */}
          <div className="card mb-3">
            <div className="card-body">
              <label className="form-label">Ajouter un article</label>
              <div className="d-flex gap-2 position-relative">
                <input
                    ref={inputRef}
                    className="form-control"
                    placeholder="Code-barres ou nom (préfixe)…"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && searchItem()}
                  />
                <button className="btn btn-outline-secondary" type="button" onClick={searchItem}>
                  {searching ? "…" : "Chercher"}
                </button>

                {sugs.length > 0 && (
                  <div
                    className="list-group position-absolute w-100 mt-5 shadow"
                    style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                  >
                    {sugs.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="list-group-item list-group-item-action"
                        onClick={() => pickSuggestion(s)}
                      >
                        <div className="fw-semibold">{s.name}</div>
                        <small className="text-muted">
                          CB: {s.barcode || "—"} · Coût: {Number(s.unitCost || s.cost || 0).toLocaleString()} {CURRENCY}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-text">Tu peux associer un <code>PO ID</code> par ligne pour incrémenter le <code>shippedQty</code> du PO correspondant.</div>
            </div>
          </div>

          {/* Lignes libres */}
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Article</th>
                      <th>CB</th>
                      <th style={{ width: 140 }} className="text-end">À expédier</th>
                      <th style={{ width: 200 }}>PO (optionnel)</th>
                      <th className="text-end">Coût ({CURRENCY})</th>
                      <th className="text-end">Total ({CURRENCY})</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeLines.length === 0 ? (
                      <tr><td colSpan={7} className="text-muted">Aucune ligne</td></tr>
                    ) : freeLines.map((l) => {
                      const q = Number(l.qtyToShip || 0);
                      const total = q * Number(l.unitCost || 0);
                      return (
                        <tr key={l.rowId}>
                          <td>
                            <div className="fw-semibold">{l.name}</div>
                            <div className="small text-muted">{l.itemId}</div>
                          </td>
                          <td>{l.barcode || "—"}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control text-end"
                              value={l.qtyToShip}
                              onChange={(e) =>
                                setFreeLines((prev) =>
                                  prev.map((x) =>
                                    x.rowId === l.rowId ? { ...x, qtyToShip: Number(e.target.value || 0) } : x
                                  )
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              className="form-control"
                              placeholder="PO ID si lié"
                              value={l.poId || ""}
                              onChange={(e) =>
                                setFreeLines((prev) =>
                                  prev.map((x) => (x.rowId === l.rowId ? { ...x, poId: e.target.value } : x))
                                )
                              }
                            />
                            <div className="form-text">Laisser vide si hors-PO.</div>
                          </td>
                          <td className="text-end">{Number(l.unitCost || 0).toLocaleString()}</td>
                          <td className="text-end fw-semibold">{total.toLocaleString()}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setFreeLines((prev) => prev.filter((x) => x.rowId !== l.rowId))}
                            >
                              Retirer
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="text-end">Totaux</td>
                      <td className="text-end fw-bold">{totalsFree.qty.toLocaleString()}</td>
                      <td></td>
                      <td></td>
                      <td className="text-end fw-bold">{totalsFree.cost.toLocaleString()} {CURRENCY}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {msg && (
                <div
                  className={`mt-3 alert py-2 ${
                    msg.startsWith("✅") ? "alert-success" : msg.startsWith("❌") ? "alert-danger" : "alert-info"
                  }`}
                >
                  {msg}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
